import type { ReportResponseDto } from '@api/WhisApi/generated';
import { buildCsvContent } from '@utils/csv';
import type {
  CsvColumn,
  CsvValue,
} from './useReportsCsvExport.types';

export const getLocalizedValue = (
  values: Record<string, string> | undefined,
  language: string,
): string => {
  if (!values) {
    return '';
  }

  const localized = values[language];
  if (typeof localized === 'string' && localized.trim().length > 0) {
    return localized;
  }

  const fallback = Object.values(values).find(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  );

  return fallback ?? '';
};

export const formatDateOnly = (value: string | undefined): string => {
  if (!value || value.trim().length === 0) {
    return '';
  }

  const isoDateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (isoDateMatch) {
    return isoDateMatch[0];
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toISOString().slice(0, 10);
};

export const normalizeCsvValue = (value: unknown): CsvValue => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value as CsvValue;
};

export const buildReportCsvContent = (
  reports: ReportResponseDto[],
  columns: CsvColumn[],
): string => {
  const headers = columns.map((column) => column.header);

  const rows = reports.map((report) => {
    return columns.map((column) => normalizeCsvValue(column.value(report)));
  });

  return buildCsvContent(headers, rows);
};
